import { CircleCheck, UserX } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/design-system/ui/Dialog';
import { formatNumericDate } from '@escala/domain/dates';
import { PRIVACY_NOTE_MAX } from '@escala/domain/privacy';
import { publicApi } from '@/services/api/public';

import { authErrorMessage } from '../authError';

type Credentials = { email: string; password: string; org?: string };

/**
 * O aviso do login para quem foi inativado (`member_inactive`, só depois de a senha
 * conferir): o acesso acabou e, se a pessoa quiser, "Pedir a exclusão dos meus dados" abre
 * o pedido de Exclusão dos titulares — o mesmo que a administração atende em
 * Configurações › Privacidade, anonimizando o cadastro. O e-mail e a senha digitados
 * confirmam quem pede.
 */
export default function InactiveNotice({ credentials }: { credentials: Credentials }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);

  const send = async () => {
    setBusy(true);
    setError(null);

    try {
      const request = await publicApi.requestInactiveDeletion({ ...credentials, note: note.trim() });

      setDueDate(request.dueDate);
      setOpen(false);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Alert tone="warning" role="alert" className="flex-col gap-2.5">
        <span className="flex gap-2.5">
          <UserX aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-status-partial" />
          <span className="text-pretty">
            <strong className="font-semibold">Seu cadastro nesta igreja está inativo.</strong> Por isso o
            acesso ao Live Gospel foi encerrado. Para voltar, fale com a administração da igreja.
          </span>
        </span>
        {dueDate ? (
          <span role="status" className="flex gap-2.5 font-medium text-status-complete">
            <CircleCheck aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8]" />
            Pedido de exclusão enviado. A igreja responde até {formatNumericDate(dueDate)}.
          </span>
        ) : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="self-start"
            onClick={() => setOpen(true)}
          >
            Pedir a exclusão dos meus dados
          </Button>
        )}
      </Alert>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent described className="w-[min(480px,calc(100vw-24px))]">
          <DialogHeader
            title="Excluir meus dados"
            description="A administração da igreja recebe o pedido e responde em até 15 dias."
          />
          <DialogBody className="gap-3.5">
            <Alert tone="danger" role="note">
              Seus dados pessoais serão anonimizados: nome, contatos, endereço e o que mais identifica você. O
              histórico de escalas continua só em estatísticas, sem identificar você. Dados que a lei exige
              manter serão preservados pelo prazo legal.
            </Alert>
            <label className="flex flex-col gap-1.5">
              <span className="text-control font-semibold">Observação (opcional)</span>
              <textarea
                rows={3}
                maxLength={PRIVACY_NOTE_MAX}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setError(null);
                }}
                className="w-full min-w-0 resize-y rounded-control border border-line-input bg-white px-3 py-2.5 text-control text-ink placeholder:text-ink-placeholder"
              />
            </label>
            {error ? <Alert tone="danger">{error}</Alert> : null}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button variant="danger" disabled={busy} onClick={send}>
              Enviar pedido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
